// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useMemo, useContext, useEffect } from 'react';
import './SupplierOverview.scss';
import { format, subMonths, startOfDay, endOfDay } from 'date-fns';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

// Import tab components
import OverviewTab from './OverviewTab';
import DeliveriesTab from './DeliveriesTab';
import PlantProcessTab from './PlantProcessTab';
import IssuesTab from './IssuesTab';

import {
  getSupplierNumberOfDeliveries,
  getSupplierTotalWeightTons,
  getSupplierNumberOfClaims,
} from "./SupplierDataService";

// Import Material UI icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BarChartIcon from '@mui/icons-material/BarChart';
import FactoryIcon from '@mui/icons-material/Factory';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';

function SupplierOverview({
  supplier,
  supplierData,
  supplierDataFiltered,
  isLoading = false,
  activeFilters = {},
  onSupplierClick,
  chartViewMode,
  onChartViewModeChange,
}) {
  // All hooks need to be at the top level, before any conditionals
  const { t } = useTranslation();
  const { labelsets, logEventCustom } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview-tab');
  // Local material filter state
  const [useFilteredData, setUseFilteredData] = useState(true);

  // Update local filter when prop changes (when component is first rendered or parent filter changes)
  useEffect(() => {
    if (!activeFilters) {
      setUseFilteredData(false);
    } else if (activeFilters?.material == 'all' && (activeFilters?.selectedClaimIssues || []).length == 0) {
      setUseFilteredData(false);
    } else {
      setUseFilteredData(true);
    }
  }, [activeFilters]);

  function handleStopUsingFilteredData() {
    setUseFilteredData(false);
    logEventCustom('click_event', {content_type: 'remove_supplier_filter'});
  }
  
  // Use date range from props or default to 6 months if not provided
  const endDate = useMemo(() => 
    activeFilters?.dateRange?.endDate ? endOfDay(new Date(activeFilters?.dateRange?.endDate)) : endOfDay(new Date()), 
  [activeFilters?.dateRange?.endDate]);
  
  const startDate = useMemo(() => 
    activeFilters?.dateRange?.startDate ? startOfDay(new Date(activeFilters?.dateRange?.startDate)) : startOfDay(subMonths(endDate, 6)),
  [activeFilters?.dateRange?.startDate, endDate]);
  
  const dateRangeText = useMemo(
    () => `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}`,
    [startDate, endDate]
  );
  
  // Early return after all hooks
  if (!supplier) return null;

  function handleTabChange(tab) {
    setActiveTab(tab);
    logEventCustom('select_content', {content_type: 'supplier_insights_tab', content_id: tab});
  };
  
  // Function to get material display name
  function getMaterialDisplayName(materialKey) {
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  }

  function formatTons(tons) {
    if (!tons) return 0;
    return `${tons.toFixed(1).toLocaleString('de-CH')} t`.replace(',', "'");
  } 
  
  // Helper function to render a metric with optional material-specific value
  function renderMetricValue(totalValue, materialValue, formatter = val => val) {
    if (!useFilteredData) {
      return formatter(totalValue);
    }
    
    return (
      <>
        {formatter(materialValue)}
        <span className="total-value-small">{` / ${formatter(totalValue)}`}</span>
      </>
    );
  };
  
  // Calculating values from supplierData
  const deliveriesCount = getSupplierNumberOfDeliveries(supplierData);
  const issuesCount = getSupplierNumberOfClaims(supplierData);
  const totalWeight = getSupplierTotalWeightTons(supplierData);

  const filteredDeliveriesCount = getSupplierNumberOfDeliveries(supplierDataFiltered);
  const filteredIssuesCount = getSupplierNumberOfClaims(supplierDataFiltered);
  const filteredTotalWeight = getSupplierTotalWeightTons(supplierDataFiltered);
  
  return (
    <div className="supplier-overview">
      {isLoading ? (
        <div className="supplier-details-loading">
          <p>{t('loading_supplier_details')}</p>
        </div>
      ) : (
        <>
          <div className="supplier-header">
            <div className="supplier-header-main">
              <h3 className="supplier-name">{supplier.display_name || supplier.name}</h3>
              {supplier.supplier_code && (
                <p className="supplier-description">{t('code')}: {supplier.supplier_code}</p>
              )}
            </div>
            
            <div className="supplier-materials">
              {supplierData.materials && supplierData.materials.map((material, index) => (
                <span key={index} className="material-filter-badge">
                  {getMaterialDisplayName(material)}
                </span>
              ))}
            </div>
          </div>
          
          <div className="supplier-date-range">
            <CalendarTodayIcon className="icon" />
            <span>{t('data_range')}: {dateRangeText}</span>
            {useFilteredData && (
              <span className="filter-info">
                {t('filter_active')}
                <Tooltip title={t('remove_filter')} arrow>
                  <span 
                    className="filter-remove-icon" 
                    onClick={handleStopUsingFilteredData}
                  >
                    <CloseIcon className="icon" />
                  </span>
                </Tooltip>
              </span>
            )}
          </div>
          
          <div className="supplier-metrics">
            <div className="metric-card">
              <div className="metric-title">{t('deliveries')}</div>
              <div className="metric-value">
                {renderMetricValue(deliveriesCount, filteredDeliveriesCount)}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-title">{t('claims')}</div>
              <div className={`metric-value ${
                (useFilteredData ? filteredIssuesCount : issuesCount) > 3 ? "text-red" : 
                (useFilteredData ? filteredIssuesCount : issuesCount) > 0 ? "text-yellow" : 
                "text-green"
              }`}>
                {renderMetricValue(issuesCount, filteredIssuesCount)}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-title">{t('total_tonnage')}</div>
              <div className="metric-value">
                {renderMetricValue(formatTons(totalWeight), formatTons(filteredTotalWeight))}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-title">{t('rank')}</div> 
              <div className="metric-value coming-soon-badge">{t('coming_soon')}</div>
            </div>
          </div>
          
          <div className="tabs-container">
            <div className="tabs-header">
              <button 
                className={`tab-button ${activeTab === 'overview-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('overview-tab')}
              >
                <BarChartIcon className="icon" />
                <span>{t('overview')}</span>
              </button>
              <button 
                className={`tab-button ${activeTab === 'deliveries-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('deliveries-tab')}
              >
                <LocalShippingIcon className="icon" />
                <span>{t('deliveries')}</span>
              </button>
              <button 
                className={`tab-button ${activeTab === 'plant-process-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('plant-process-tab')}
              >
                <FactoryIcon className="icon" />
                <span>{t('plant_process')}</span>
              </button>
              <button 
                className={`tab-button ${activeTab === 'issues-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('issues-tab')}
              >
                <WarningAmberIcon className="icon" />
                <span>{useFilteredData ? `${t('issues')} (${filteredIssuesCount})` : `${t('issues')} (${issuesCount})`}</span>
              </button>
            </div>
            
            <div className={`tab-content ${activeTab}`}>
              {activeTab === 'overview-tab' && (
                <OverviewTab 
                  supplier={supplier} 
                  supplierData={supplierData} 
                  startDate={startDate} 
                  endDate={endDate}
                  materialFilter={useFilteredData ? activeFilters?.material || 'all' : 'all'}
                  onSupplierClick={onSupplierClick}
                  chartViewMode={chartViewMode}
                  onChartViewModeChange={onChartViewModeChange}
                />
              )}
              
              {activeTab === 'deliveries-tab' && (
                <DeliveriesTab 
                  supplier={supplier} 
                  supplierData={useFilteredData ? supplierDataFiltered : supplierData}
                  activeFilters={useFilteredData ? activeFilters : {}}
                />
              )}
              
              {activeTab === 'plant-process-tab' && (
                <PlantProcessTab 
                  supplier={supplier} 
                  supplierData={supplierData}
                  dateRange={{
                    startDate: startDate,
                    endDate: endDate
                  }}
                  activeFilters={useFilteredData ? activeFilters : {}}
                />
              )}
              
              {activeTab === 'issues-tab' ? (
                <IssuesTab 
                  supplier={supplier} 
                  supplierData={useFilteredData ? supplierDataFiltered : supplierData}
                  activeFilters={useFilteredData ? activeFilters : {}}
                />
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SupplierOverview; 